/**
 * Symbol Cache Service
 *
 * Fetches complete US stock symbol list from Cloud Function (backed by Redis).
 * Implements session-scoped in-memory caching for instant autocomplete.
 *
 * Performance:
 * - In-memory cache hit: <1ms (instant, after first load)
 * - Cloud Function (Redis): ~10-20ms (first load per session)
 * - FMP API fallback: ~500ms (only if Redis unavailable)
 *
 * Architecture:
 * Redis (7-day TTL, weekly refresh) → Cloud Function → In-Memory (session-scoped)
 *
 * Why no localStorage?
 * - Redis already caches for 7 days (source of truth)
 * - localStorage 24hr cache would serve stale data
 * - In-memory cache cleared on page refresh = always fresh from Redis
 */

import { gatewayFetch } from './gateway';

export interface SymbolEntry {
  symbol: string;
  name: string;
  exchange: string;
  sector: string | null;
  industry: string | null;
  marketCap: number | null;
  type: string;
}

export interface SymbolData {
  symbols: SymbolEntry[];
  count: number;
  sectorCounts: Record<string, number>;
  exchanges: string[];
}

export interface SearchResult {
  symbol: string;
  name: string;
  exchange: string;
  sector?: string;
  marketCap?: number;
}

// Configuration - Use gateway with service=fmp&action=symbols-list

// In-memory cache (session-scoped, fastest)
let memoryCache: SymbolData | null = null;
let symbolMap: Map<string, SymbolEntry> | null = null; // For O(1) symbol lookup
let sectorMap: Map<string, SymbolEntry[]> | null = null; // For sector filtering

/**
 * Load symbol database from Cloud Function (Redis-backed).
 * Automatically called on app init, can also be called manually.
 */
// The request in flight, shared by every caller that arrives before it lands.
// The cache below is set only after the await, so two callers on one page load
// (the research page and search, on the same tick) each fetched the whole
// list ([[BUGS #511]]).
let inFlight: Promise<void> | null = null;

export function loadSymbolDatabase(): Promise<void> {
  if (memoryCache) return Promise.resolve();
  if (!inFlight) {
    inFlight = fetchSymbolDatabase().finally(() => { inFlight = null; });
  }
  return inFlight;
}

async function fetchSymbolDatabase(): Promise<void> {
  console.log('[SymbolCache] Loading symbol database...');

  // Fetch from Cloud Function (Redis-backed, ~10-20ms)
  try {
    console.log('[SymbolCache] Fetching from Cloud Function (Redis)...');

    // Add timeout to prevent long waits if Redis/Cloud Function is slow
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await gatewayFetch('fmp', 'symbols-list', {
      auth: 'none',
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Cloud Function error: ${response.status}`);
    }

    const data: SymbolData = await response.json();

    // Validate response
    if (!data.symbols || !Array.isArray(data.symbols)) {
      throw new Error('Invalid response from Cloud Function');
    }

    console.log(`[SymbolCache] Loaded ${data.count} symbols from Redis (via Cloud Function)`);

    // Cache in memory for session
    memoryCache = data;
    buildMemoryIndexes(data.symbols);

  } catch (error) {
    console.error('[SymbolCache] Failed to load symbol database:', error);
    // Don't throw - let search fall back to FMP API
  }
}

/**
 * Build in-memory indexes for fast lookups.
 */
function buildMemoryIndexes(symbols: SymbolEntry[]): void {
  // Symbol map: symbol → entry (for exact lookups)
  symbolMap = new Map();
  sectorMap = new Map();

  for (const entry of symbols) {
    symbolMap.set(entry.symbol, entry);

    // Sector map (for sector filtering)
    const sector = entry.sector || 'Unknown';
    if (!sectorMap.has(sector)) {
      sectorMap.set(sector, []);
    }
    sectorMap.get(sector)!.push(entry);
  }

  console.log(`[SymbolCache] Built indexes: ${symbolMap.size} symbols, ${sectorMap.size} sectors`);
}

/**
 * Search symbols with instant in-memory lookup.
 * 3-tier ranking: exact match > prefix match > name contains.
 */
export async function searchSymbols(
  query: string,
  sector?: string,
  limit: number = 10
): Promise<SearchResult[]> {
  // Ensure cache is loaded
  if (!memoryCache || !symbolMap) {
    await loadSymbolDatabase();
  }

  if (!symbolMap) {
    console.warn('[SymbolCache] No cache available, returning empty');
    return [];
  }

  const upperQuery = query.toUpperCase().trim();
  const results: SearchResult[] = [];

  // Tier 1: Exact symbol match
  const exactMatch = symbolMap.get(upperQuery);
  if (exactMatch && (!sector || exactMatch.sector === sector)) {
    results.push(toSearchResult(exactMatch));
    if (results.length >= limit) return results;
  }

  // Tier 2: Symbol prefix match
  const prefixMatches: SymbolEntry[] = [];
  const nameMatches: SymbolEntry[] = [];

  // Convert Map values to array for iteration
  const allEntries = Array.from(symbolMap.values());

  for (const entry of allEntries) {
    if (sector && entry.sector !== sector) continue;

    if (entry.symbol.startsWith(upperQuery) && entry.symbol !== upperQuery) {
      prefixMatches.push(entry);
    } else if (entry.name.toUpperCase().includes(upperQuery)) {
      nameMatches.push(entry);
    }
  }

  // Sort by market cap (higher first)
  prefixMatches.sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));
  nameMatches.sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));

  // Add prefix matches
  for (const entry of prefixMatches) {
    results.push(toSearchResult(entry));
    if (results.length >= limit) return results;
  }

  // Add name matches
  for (const entry of nameMatches) {
    results.push(toSearchResult(entry));
    if (results.length >= limit) return results;
  }

  return results;
}

/**
 * Get top N companies by market cap.
 * Used for empty search (show top companies).
 */
export async function getTopCompaniesByMarketCap(
  sector?: string,
  limit: number = 50
): Promise<SearchResult[]> {
  // Ensure cache is loaded
  if (!memoryCache || !symbolMap) {
    await loadSymbolDatabase();
  }

  if (!symbolMap) {
    console.warn('[SymbolCache] No cache available, returning empty');
    return [];
  }

  // Filter by sector if provided
  let candidates: SymbolEntry[] = [];

  if (sector && sectorMap) {
    candidates = sectorMap.get(sector) || [];
  } else {
    candidates = Array.from(symbolMap.values());
  }

  // Filter out entries without market cap, then sort
  const withMarketCap = candidates.filter(e => e.marketCap && e.marketCap > 0);
  withMarketCap.sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));

  return withMarketCap.slice(0, limit).map(toSearchResult);
}

/**
 * Convert SymbolEntry to SearchResult format.
 */
function toSearchResult(entry: SymbolEntry): SearchResult {
  return {
    symbol: entry.symbol,
    name: entry.name,
    exchange: entry.exchange,
    sector: entry.sector || undefined,
    marketCap: entry.marketCap || undefined
  };
}

/**
 * Initialize symbol database in background on app load.
 * Non-blocking, logs errors but doesn't throw.
 */
export function initializeSymbolDatabase(): void {
  loadSymbolDatabase().catch(error => {
    console.error('[SymbolCache] Background initialization failed:', error);
  });
}

/**
 * Clear in-memory cache (for testing/debugging).
 * Cache is automatically cleared on page refresh.
 */
export function clearCache(): void {
  memoryCache = null;
  symbolMap = null;
  sectorMap = null;
  console.log('[SymbolCache] In-memory cache cleared');
}
